import { colors } from "@indeed/ifl-tokens";
import React, { type ReactNode, useCallback, useMemo, useState, forwardRef } from "react";
import {
  type StyleProp,
  StyleSheet,
  type TextStyle,
  type ViewStyle,
  type AccessibilityRole,
  type AccessibilityState,
} from "react-native";

// Note: This file is a copy of the Button component from the IFL library.
import { Box, Pressable, Text } from "../../../../mads";
import { useI18n } from "../../../I18n";

type CommonSize = "sm" | "md" | "lg";

/**
 * Primary font family for React Native components
 */
const FONT_FAMILY = "Indeed Sans";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "text"
  | "inverse"
  | "destructivePrimary"
  | "destructiveSecondary"
  | "destructiveTertiary"
  | "destructiveText"
  | "utility";

export type ButtonSize = CommonSize;

const VARIANT_STYLES: Record<ButtonVariant, ViewStyle> = {
  primary: { backgroundColor: colors.background.action.primary["[base]"] },
  secondary: {
    backgroundColor: colors.background.action["[base]"],
    borderColor: colors.border.action.weak["[base]"],
    borderStyle: "solid",
    borderWidth: 1,
  },
  tertiary: { backgroundColor: "transparent" },
  text: { backgroundColor: "transparent" },
  inverse: {
    backgroundColor: colors.background.action.transparent["[base]"],
    borderColor: colors.border.inverse["[base]"],
    borderStyle: "solid",
    borderWidth: 1,
  },
  destructivePrimary: { backgroundColor: colors.background.action.destructive["[base]"] },
  destructiveSecondary: {
    backgroundColor: colors.background.action.active,
    borderColor: colors.border.weak,
    borderStyle: "solid",
    borderWidth: 1,
  },
  destructiveTertiary: { backgroundColor: "transparent" },
  destructiveText: { backgroundColor: "transparent" },
  utility: {
    backgroundColor: colors.background.action.weak["[base]"],
  },
};

const COLORS: Record<ButtonVariant, string> = {
  primary: colors.foreground.inverse["[base]"],
  secondary: colors.foreground.action.primary["[base]"],
  tertiary: colors.foreground.action.primary["[base]"],
  text: colors.foreground.action.primary["[base]"],
  inverse: colors.foreground.inverse["[base]"],
  destructivePrimary: colors.foreground.inverse["[base]"],
  destructiveSecondary: colors.foreground.action.destructive["[base]"],
  destructiveTertiary: colors.foreground.action.destructive["[base]"],
  destructiveText: colors.foreground.action.destructive["[base]"],
  utility: colors.foreground.strong,
};

const styles = StyleSheet.create({
  base: {
    borderRadius: 8,
    padding: 13,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
  },
  disabled: {
    opacity: 0.4,
    pointerEvents: "none",
  },
});

const ACTIVE_STYLES: Record<ButtonVariant, ViewStyle> = {
  primary: {
    backgroundColor: colors.background.action.primary.active,
  },
  secondary: {
    backgroundColor: colors.background.action.active,
    borderColor: colors.border.action.weak.active,
  },
  tertiary: {
    backgroundColor: colors.background.action.active,
  },
  text: {},
  inverse: {
    backgroundColor: colors.background.action.transparent.active,
  },
  destructivePrimary: {
    backgroundColor: colors.background.action.destructive.active,
  },
  destructiveSecondary: {
    backgroundColor: colors.background.action.weak.active,
    borderColor: colors.border.action.weak.active,
  },
  destructiveTertiary: {
    backgroundColor: colors.background.action.active,
  },
  destructiveText: {},
  utility: {
    backgroundColor: colors.background.action.weak.active,
  },
};

const paddingSizes = {
  sm: 5,
  md: 9,
  lg: 13,
};

const dimensions = {
  sm: 36,
  md: 44,
  lg: 52,
};

export interface ButtonProps {
  testID?: string;

  /**
   * Change the style type of the button. Choose between `primary`, `secondary`, `tertiary`, `text`, `utility` and `inverse`, or destructive variants `destructivePrimary`, `destructiveSecondary`, `destructiveTertiary`, and `destructiveText`.
   * This can also be an array of sizes for responsive layouts.
   */
  variant?: ButtonVariant;

  /**
   * The content of the button
   */
  children?: React.ReactNode;

  /**
   * The callback for when the button is pressed
   */
  onPress: () => void;

  /**
   * custom styling
   */
  sx?: StyleProp<ViewStyle>;

  /**
   * custom text styling
   */
  textSx?: TextStyle;

  /**
   * custom afterIcon styling
   */
  afterIconSx?: ViewStyle;

  /**
   * Adds an icon to appear after (to the right of) the link text.
   */
  iconAfter?: ReactNode;

  /**
   * Adds an icon to appear before (to the left of) the button text.
   */
  iconBefore?: ReactNode;

  /**
   * Set to true to set styling for buttons with only an icon as the child.
   * Only use with `primary`, `secondary`, `tertiary`, `utility`, and `inverse` variants.
   */
  iconOnly?: boolean;

  /**
   * Whether the content for the button is loading
   * the press action cannot be triggered when this is set to true.
   */
  loading?: boolean;

  /**
   * Disables button interaction and applies disabled state styles.
   */
  disabled?: boolean;

  ariaLabel?: string;
  /**
   * Size of the button. Use the sizings of `sm`, `md`, and `lg`.
   */
  size?: ButtonSize;
  /**
   * Accessibility role for the button (e.g., 'radio' for radio buttons)
   */
  accessibilityRole?: AccessibilityRole;
  /**
   * Accessibility state for the button (e.g., { selected: true } for radio buttons)
   */
  accessibilityState?: AccessibilityState;
}

export const getSizeStyles = (size: ButtonSize, iconOnly: boolean): ViewStyle => {
  return {
    paddingTop: paddingSizes[size],
    paddingBottom: paddingSizes[size],
    paddingLeft: iconOnly ? paddingSizes[size] : 16,
    paddingRight: iconOnly ? paddingSizes[size] : 16,
    minWidth: iconOnly ? dimensions[size] : undefined,
    minHeight: dimensions[size],
  };
};

export const Button = forwardRef<React.ElementRef<typeof Pressable>, ButtonProps>(
  (
    {
      testID,
      ariaLabel,
      variant = "primary",
      children,
      onPress,
      sx = {},
      textSx = {},
      afterIconSx = {},
      iconBefore = null,
      iconAfter,
      loading = false,
      disabled = false,
      iconOnly = false,
      size = "md",
      accessibilityRole,
      accessibilityState,
    }: ButtonProps,
    ref,
  ) => {
    const i18n = useI18n();
    const [isActive, setIsActive] = useState(false);
    const style: StyleProp<ViewStyle> = useMemo(
      () => [
        styles.base,
        VARIANT_STYLES[variant],
        getSizeStyles(size, iconOnly),
        sx,
        disabled && { opacity: 0.4 },
        isActive && ACTIVE_STYLES[variant],
      ],
      [variant, sx, disabled, size, iconOnly, isActive],
    );

    const hasIcon = !!iconAfter;

    const textStyle: StyleProp<TextStyle> = useMemo(
      () => ({
        color: COLORS[variant],
        fontWeight: "bold",
        fontFamily: FONT_FAMILY,
        fontSize: 16,
        marginRight: hasIcon ? 8 : 0,
        ...textSx,
      }),
      [variant, hasIcon, textSx],
    );

    const press = useCallback(() => {
      if (disabled) {
        return;
      }

      onPress();
    }, [onPress, disabled]);

    return (
      <Pressable
        ref={ref}
        testID={testID}
        sx={style}
        onPress={press}
        disabled={loading || disabled}
        aria-label={ariaLabel}
        accessibilityRole={accessibilityRole ?? "button"}
        {...(accessibilityState && { accessibilityState })}
        onPressIn={() => setIsActive(true)}
        onPressOut={() => setIsActive(false)}
      >
        {iconBefore && <Box sx={{ marginRight: 8 }}>{iconBefore}</Box>}
        {!iconOnly &&
          (typeof children === "string" ? (
            <Text sx={textStyle}>{loading ? i18n.gettext("Loading") : children}</Text>
          ) : (
            children
          ))}
        {hasIcon && <Box sx={afterIconSx}>{iconAfter}</Box>}
      </Pressable>
    );
  },
);

Button.displayName = "Button";
